import React, { useState } from 'react';
import { Search, User, Users, X } from 'lucide-react';
import { Input } from '../ui/input';
import ChatModalPortal from './ChatModalPortal';
import { useLanguage } from '../../contexts/LanguageContext';

export const NewChatModal = ({
  open,
  onClose,
  users = [],
  currentUserId,
  onlineUserIds = new Set(),
  loadError = '',
  onStartDirectChat,
  onCreateGroup,
}) => {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('direct');
  const [groupName, setGroupName] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [startingUserId, setStartingUserId] = useState(null);
  const [groupError, setGroupError] = useState('');

  if (!open) return null;

  const getDisplayName = (profile) => (
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Student'
  );

  const filteredUsers = users.filter((profile) => {
    if (profile.id === currentUserId) return false;
    const query = search.toLowerCase();
    return `${getDisplayName(profile)} ${profile.email || ''} ${profile.profession || ''}`
      .toLowerCase()
      .includes(query);
  });

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : name.slice(0, 2).toUpperCase();
  };

  return (
    <ChatModalPortal>
      <div className="fixed inset-0 z-[1000] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border-0 bg-white shadow-2xl">
        <div aria-hidden="true" className="flex h-1.5 w-full">
          <span className="flex-1 bg-zinc-950" />
          <span className="flex-1 bg-[#DD0000]" />
          <span className="flex-1 bg-[#FFCE00]" />
        </div>
        <div className="flex items-center justify-between border-b border-zinc-100 bg-white px-5 py-4">
          <div>
            <h3 className="text-base font-extrabold tracking-tight text-zinc-950">
              {t(mode === 'group' ? 'Krijo grup' : 'Bisedë e re')}
            </h3>
            <p className="mt-0.5 text-xs text-zinc-500">
              {t(mode === 'group' ? 'Emërto grupin dhe zgjidh anëtarët.' : 'Zgjidhni një person për bisedë direkte')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Mbyll dritaren"
            title="Mbyll"
            className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"
          >
            <X size={18} />
          </button>
        </div>

        {onCreateGroup && (
          <div className="grid grid-cols-2 gap-1 border-b border-zinc-100 p-2">
            <button
              type="button"
              onClick={() => { setMode('direct'); setGroupError(''); }}
              className={`rounded-xl px-3 py-2 text-xs font-bold transition ${mode === 'direct' ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:bg-white/70'}`}
            >
              {t('Bisedë direkte')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('group'); setGroupError(''); }}
              className={`rounded-xl px-3 py-2 text-xs font-bold transition ${mode === 'group' ? 'bg-zinc-950 text-white shadow-sm' : 'text-zinc-500 hover:bg-white/70'}`}
            >
              {t('Grup')}
            </button>
          </div>
        )}

        {mode === 'group' && (
          <div className="border-b border-zinc-100 px-3 pt-3">
            <Input
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              placeholder={t('Emri i grupit')}
              className="h-9 rounded-xl border-zinc-200 bg-zinc-50 text-xs"
              maxLength={120}
            />
          </div>
        )}

        <div className="border-b border-zinc-100 p-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('Kërko sipas emrit...')}
              className="h-9 rounded-xl border-zinc-200 bg-zinc-50 pl-9 text-xs"
            />
          </div>
        </div>

        <div className="flex-1 space-y-1 overflow-y-auto p-2">
          {loadError ? (
            <div role="alert" className="px-4 py-8 text-center text-xs text-red-600">{loadError}</div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-8 text-center text-xs text-zinc-400">
              {users.filter((profile) => profile.id !== currentUserId).length === 0
                ? t('Nuk u gjet asnjë profil tjetër.')
                : t('Nuk u gjet asnjë përdorues.')}
            </div>
          ) : (
            filteredUsers.map((profile) => {
              const isSelected = selectedUserIds.includes(profile.id);
              return (
                <button
                  key={profile.id}
                  type="button"
                  disabled={startingUserId !== null || creatingGroup}
                  onClick={() => {
                    if (mode === 'direct') {
                      setStartingUserId(profile.id);
                      setGroupError('');
                      Promise.resolve(onStartDirectChat(profile))
                        .then((started) => {
                          if (started === false) setGroupError(t('Biseda nuk u hap. Provoni përsëri.'));
                          else onClose();
                        })
                        .catch((error) => {
                          console.error('Unable to start direct chat:', error);
                          setGroupError(t('Biseda nuk u hap. Provoni përsëri.'));
                        })
                        .finally(() => setStartingUserId(null));
                      return;
                    }
                    setSelectedUserIds((current) => (
                      current.includes(profile.id)
                        ? current.filter((id) => id !== profile.id)
                        : [...current, profile.id]
                    ));
                    setGroupError('');
                  }}
                  className={`group flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition disabled:cursor-wait disabled:opacity-60 ${isSelected ? 'border-amber-200 bg-amber-50' : 'border-transparent hover:border-zinc-100 hover:bg-zinc-50'}`}
                >
                  <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-bold text-white ring-1 ring-zinc-200">
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="" className="h-full w-full rounded-full object-cover" />
                    ) : getInitials(getDisplayName(profile))}
                    <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white ${onlineUserIds.has(profile.id) ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-bold text-zinc-900">
                      {getDisplayName(profile)}
                    </p>
                    <p className="truncate text-[11px] text-zinc-500">
                      {t(profile.profession || 'Student i Gjermanishtes')}
                    </p>
                  </div>
                  {mode === 'group' ? (
                    <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${isSelected ? 'border-zinc-500 bg-zinc-900 text-white' : 'border-zinc-300 text-transparent'}`}>
                      <Users size={12} />
                    </span>
                  ) : startingUserId === profile.id ? <span className="text-[10px] font-semibold text-zinc-500">{t('Duke hapur...')}</span> : <User size={15} className="text-zinc-400" />}
                </button>
              );
            })
          )}
        </div>
        {mode === 'direct' && groupError && (
          <p role="alert" className="border-t border-zinc-100 px-4 py-2 text-xs text-red-600">{t(groupError)}</p>
        )}
        {mode === 'group' && (
          <div className="border-t border-zinc-100 p-3">
            {groupError && <p role="alert" className="mb-2 text-xs text-red-600">{t(groupError)}</p>}
            <button
              type="button"
              disabled={creatingGroup || !groupName.trim() || selectedUserIds.length === 0}
              onClick={async () => {
                setCreatingGroup(true);
                setGroupError('');
                try {
                  const created = await onCreateGroup({
                    name: groupName.trim(),
                    memberIds: selectedUserIds,
                  });
                  if (!created) {
                    setGroupError(t('Grupi nuk u krijua. Kontrolloni lidhjen dhe provoni përsëri.'));
                    return;
                  }
                  setGroupName('');
                  setSelectedUserIds([]);
                  setSearch('');
                  onClose();
                } catch (error) {
                  console.error('Unable to create group chat:', error);
                  setGroupError(t('Grupi nuk u krijua. Provoni përsëri.'));
                } finally {
                  setCreatingGroup(false);
                }
              }}
              className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-zinc-950 text-xs font-bold text-white transition hover:bg-[#DD0000] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Users size={14} />
              {creatingGroup ? t('Duke krijuar grupin...') : t(`Krijo grup${selectedUserIds.length ? ` (${selectedUserIds.length})` : ''}`)}
            </button>
          </div>
        )}
      </div>
      </div>
    </ChatModalPortal>
  );
};

export default NewChatModal;
